import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Clock, Info, UserPlus, X } from "lucide-react";
import { api } from "../../lib/api";

const NEW_BADGE_MS = 3 * 24 * 60 * 60 * 1000; // 3 days
const BAR_HEIGHT = 40; // px, per bar
const DISMISS_KEY_RED = "wlou.announcement.red.dismiss";
const DISMISS_KEY_BLUE = "wlou.announcement.blue.dismiss";
const DISMISS_KEY_WELCOME = "wlou.announcement.welcome.dismiss";
export const DEFAULT_COLORS = {
  red: { bg: "#8B1A1A", text: "#F5E9C8", accent: "#F5D97A" },
  blue: { bg: "#1E3A5F", text: "#DCE9F6", accent: "#7DB0E8" },
  welcome: { bg: "#C0A875", text: "#0D110C", accent: "#1ABC9C" },
};

/**
 * Fixed announcement stack under the Navbar on Home.
 * Two independent bars:
 *   - Red announcement bar   (enabled/text/posted_at/event_at + *New* + countdown)
 *   - Blue info bar          (info_enabled/info_text — smaller, quieter updates)
 * Each bar has a ✕ dismiss button. Dismissals are per-bar, keyed by the
 * current message content, and last until midnight local time. If the admin
 * edits the text, the key changes so the bar reappears automatically.
 */
export default function AnnouncementBar() {
  const [data, setData] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [dismissTick, setDismissTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/content/announcement")
      .then((res) => !cancelled && setData(res.data))
      .catch(() => !cancelled && setData(null));
    return () => {
      cancelled = true;
    };
  }, []);

  const eventMs = useMemo(() => {
    if (!data?.event_at) return NaN;
    const t = Date.parse(data.event_at);
    return Number.isFinite(t) ? t : NaN;
  }, [data?.event_at]);

  useEffect(() => {
    const hasFutureEvent =
      Number.isFinite(eventMs) && eventMs - Date.now() > 0;
    const interval = hasFutureEvent ? 1000 : 60_000;
    const t = setInterval(() => setNow(Date.now()), interval);
    return () => clearInterval(t);
  }, [eventMs]);

  const redKey = data
    ? `${data.posted_at || ""}::${(data.text || "").trim()}`
    : "";
  const infoKey = data ? (data.info_text || "").trim() : "";
  const welcome = data?.welcome_enabled && data?.welcome?.username ? data.welcome : null;
  const welcomeKey = welcome ? `${welcome.discord_id}::${welcome.approved_at}` : "";

  const redDismissed = isDismissed(DISMISS_KEY_RED, redKey);
  const infoDismissed = isDismissed(DISMISS_KEY_BLUE, infoKey);
  const welcomeVisible = !!welcome && !isDismissed(DISMISS_KEY_WELCOME, welcomeKey);
  void dismissTick; // re-read localStorage on tick bumps

  const redVisible = !!(
    data && data.enabled && data.text && data.text.trim() && !redDismissed
  );
  const infoVisible = !!(
    data &&
    data.info_enabled &&
    data.info_text &&
    data.info_text.trim() &&
    !infoDismissed
  );

  useEffect(() => {
    const total =
      (redVisible ? BAR_HEIGHT : 0) +
      (infoVisible ? BAR_HEIGHT : 0) +
      (welcomeVisible ? BAR_HEIGHT : 0);
    document.documentElement.style.setProperty(
      "--announcement-h",
      `${total}px`
    );
    return () => {
      document.documentElement.style.setProperty("--announcement-h", "0px");
    };
  }, [redVisible, infoVisible, welcomeVisible]);

  if (!redVisible && !infoVisible && !welcomeVisible) return null;

  const postedMs = data.posted_at ? Date.parse(data.posted_at) : NaN;
  const isNew = Number.isFinite(postedMs) && now - postedMs < NEW_BADGE_MS;

  const msLeft = Number.isFinite(eventMs) ? eventMs - now : NaN;
  const showCountdown = Number.isFinite(msLeft) && msLeft > 0;
  const cd = showCountdown ? breakdown(msLeft) : null;

  const dismissRed = () => {
    dismissUntilMidnight(DISMISS_KEY_RED, redKey);
    setDismissTick((n) => n + 1);
  };
  const dismissWelcome = () => {
    dismissUntilMidnight(DISMISS_KEY_WELCOME, welcomeKey);
    setDismissTick((n) => n + 1);
  };
  const dismissInfo = () => {
    dismissUntilMidnight(DISMISS_KEY_BLUE, infoKey);
    setDismissTick((n) => n + 1);
  };

  const colors = data.colors || DEFAULT_COLORS;
  const red = colors.red || DEFAULT_COLORS.red;
  const blue = colors.blue || DEFAULT_COLORS.blue;

  return (
    <>
      {redVisible && (
        <div
          data-testid="announcement-bar"
          className="fixed left-0 right-0 z-40 border-b border-black/30 shadow-[0_4px_12px_-4px_rgba(0,0,0,0.6)]"
          style={{ top: "72px", height: `${BAR_HEIGHT}px`, backgroundColor: red.bg }}
        >
          <div className="max-w-7xl mx-auto h-full px-6 flex items-center gap-3 overflow-hidden">
            <AlertTriangle size={14} className="shrink-0" style={{ color: red.accent }} aria-hidden="true" />
            {isNew && (
              <span
                data-testid="announcement-new-badge"
                className="shrink-0 inline-flex items-center font-mono-ui text-[10px] uppercase tracking-[0.25em] font-bold px-2 py-0.5"
                style={{ backgroundColor: red.accent, color: red.bg }}
              >
                New
              </span>
            )}
            <span
              data-testid="announcement-text"
              className="font-mono-ui text-xs sm:text-sm uppercase tracking-[0.18em] font-semibold truncate"
              style={{ color: red.text }}
            >
              {data.text}
            </span>

            {showCountdown && (
              <div
                data-testid="announcement-countdown"
                className="ml-auto shrink-0 flex items-center gap-2 pl-3 border-l border-black/30"
              >
                <Clock size={12} className="shrink-0 hidden sm:inline" style={{ color: red.accent }} aria-hidden="true" />
                <div className="flex items-baseline gap-1.5 font-mono-ui" style={{ color: red.text }}>
                  {cd.days > 0 && <TimeUnit value={cd.days} label="d" accent={red.accent} />}
                  <TimeUnit value={cd.hours} label="h" pad accent={red.accent} />
                  <TimeUnit value={cd.mins} label="m" pad accent={red.accent} />
                  {cd.days === 0 && <TimeUnit value={cd.secs} label="s" pad accent={red.accent} />}
                </div>
              </div>
            )}

            <DismissButton
              testid="announcement-dismiss"
              label="Dismiss announcement for today"
              color={red.text}
              className={showCountdown ? "" : "ml-auto"}
              onClick={dismissRed}
            />
          </div>
        </div>
      )}

      {infoVisible && (
        <div
          data-testid="info-bar"
          className="fixed left-0 right-0 z-40 border-b border-black/30 shadow-[0_4px_12px_-4px_rgba(0,0,0,0.5)]"
          style={{
            top: `${72 + (redVisible ? BAR_HEIGHT : 0)}px`,
            height: `${BAR_HEIGHT}px`,
            backgroundColor: blue.bg,
          }}
        >
          <div className="max-w-7xl mx-auto h-full px-6 flex items-center gap-3 overflow-hidden">
            <Info size={14} className="shrink-0" style={{ color: blue.accent }} aria-hidden="true" />
            <span
              data-testid="info-bar-text"
              className="font-mono-ui text-xs sm:text-sm uppercase tracking-[0.18em] font-semibold truncate"
              style={{ color: blue.text }}
            >
              {data.info_text}
            </span>
            <DismissButton
              testid="info-bar-dismiss"
              label="Dismiss info bar for today"
              color={blue.text}
              className="ml-auto"
              onClick={dismissInfo}
            />
          </div>
        </div>
      )}

      {welcomeVisible && (
        <WelcomeBar
          welcome={welcome}
          c={colors.welcome || DEFAULT_COLORS.welcome}
          top={72 + (redVisible ? BAR_HEIGHT : 0) + (infoVisible ? BAR_HEIGHT : 0)}
          onDismiss={dismissWelcome}
        />
      )}
    </>
  );
}

function DismissButton({ testid, label, color, className, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-label={label}
      title="Hide until tomorrow"
      className={`${className} shrink-0 opacity-70 hover:opacity-100 hover:bg-black/15 rounded-sm p-1 transition-[opacity,background-color]`}
      style={{ color }}
    >
      <X size={14} />
    </button>
  );
}

export function WelcomeBar({ welcome, c, top, onDismiss, preview = false }) {
  return (
    <div
      data-testid={preview ? "welcome-bar-preview" : "welcome-bar"}
      className={`${preview ? "relative" : "fixed left-0 right-0 z-40 animate-in fade-in slide-in-from-top-2 duration-500"} border-b border-black/25 shadow-[0_4px_12px_-4px_rgba(0,0,0,0.5)]`}
      style={{ top: preview ? undefined : `${top}px`, height: `${BAR_HEIGHT}px`, backgroundColor: c.bg }}
    >
      <div className={`${preview ? "px-4" : "max-w-7xl mx-auto px-6"} h-full flex items-center gap-3 overflow-hidden`}>
        <UserPlus size={14} className="shrink-0" style={{ color: c.text }} aria-hidden="true" />
        <span
          className="hidden sm:inline shrink-0 font-mono-ui text-[10px] uppercase tracking-[0.3em] font-bold opacity-70"
          style={{ color: c.text }}
        >
          // New blood
        </span>
        <span
          data-testid={preview ? undefined : "welcome-bar-text"}
          className="flex items-center gap-2 min-w-0 font-mono-ui text-xs sm:text-sm uppercase tracking-[0.18em] font-semibold whitespace-nowrap"
          style={{ color: c.text }}
        >
          <span className="shrink-0">Welcome to the wall,</span>
          <span
            data-testid={preview ? undefined : "welcome-bar-username"}
            className="shrink-0 inline-flex items-center gap-1.5 normal-case tracking-normal font-bold bg-[#0D110C] pl-0.5 pr-1.5 py-0.5"
            style={{ color: c.accent }}
          >
            {welcome.avatar && (
              <img
                src={welcome.avatar}
                alt=""
                data-testid={preview ? undefined : "welcome-bar-avatar"}
                className="w-5 h-5 rounded-full object-cover ring-1"
                style={{ "--tw-ring-color": c.accent }}
                referrerPolicy="no-referrer"
              />
            )}
            @{welcome.username}
          </span>
          {welcome.character_name && (
            <span className="truncate">
              as{" "}
              <span data-testid={preview ? undefined : "welcome-bar-character"} className="font-bold">
                {welcome.character_name}
              </span>
              <span className="hidden md:inline"> — our newest survivor.</span>
            </span>
          )}
        </span>
        {onDismiss && (
          <DismissButton
            testid="welcome-bar-dismiss"
            label="Dismiss welcome bar for today"
            color={c.text}
            className="ml-auto"
            onClick={onDismiss}
          />
        )}
      </div>
    </div>
  );
}

function TimeUnit({ value, label, pad = false, accent }) {
  const num = pad ? String(value).padStart(2, "0") : String(value);
  return (
    <span className="tabular-nums text-xs sm:text-sm font-bold tracking-wider">
      {num}
      <span className="text-[10px] font-normal ml-0.5 opacity-80" style={{ color: accent }}>
        {label}
      </span>
    </span>
  );
}

function breakdown(ms) {
  const totalSecs = Math.floor(ms / 1000);
  const days = Math.floor(totalSecs / 86400);
  const hours = Math.floor((totalSecs % 86400) / 3600);
  const mins = Math.floor((totalSecs % 3600) / 60);
  const secs = totalSecs % 60;
  return { days, hours, mins, secs };
}

// -------- dismissal helpers --------
// A bar is "dismissed" if localStorage has a record whose `key` matches the
// current message key (so admin edits reset the dismiss) AND whose `until`
// timestamp hasn't passed yet.
function isDismissed(storageKey, contentKey) {
  if (!contentKey) return false;
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.key !== contentKey) return false;
    const until = Number(parsed.until) || 0;
    return Date.now() < until;
  } catch {
    return false;
  }
}

function dismissUntilMidnight(storageKey, contentKey) {
  const now = new Date();
  const midnight = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + 1,
    0,
    0,
    0,
    0
  );
  try {
    localStorage.setItem(
      storageKey,
      JSON.stringify({ key: contentKey, until: midnight.getTime() })
    );
  } catch {
    /* ignore quota / private-mode errors */
  }
}
